import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useScrollHeader } from '@/hooks/useScrollHeader';
import { Search } from 'lucide-react';
import inzLogo from '../assets/inz-logo.png';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const isHeaderVisible = useScrollHeader();
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/posts?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed w-full z-50 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'}`} id="header" role="banner">
      <nav className="glass mx-4 my-4 rounded-2xl">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4">
              <div className="relative w-8 h-8">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg"></div>
                <img 
                  src={inzLogo} 
                  alt="InvizHer Logo" 
                  className="relative w-full h-full rounded-lg object-cover"
                  width="32"
                  height="32"
                />
              </div>
              <h1 className="text-xl font-bold gradient-text" title="InvizHer">InvizHer</h1>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6" aria-label="Main navigation">
              <Link to="/posts" className="text-gray-700 hover:text-blue-500 transition-colors">Posts</Link>
              <Link to="/projects" className="text-gray-700 hover:text-blue-500 transition-colors">Projects</Link>
              <Link to="/languages" className="text-gray-700 hover:text-blue-500 transition-colors">Languages</Link>
              <Link to="/categories" className="text-gray-700 hover:text-blue-500 transition-colors">Categories</Link>
              
              {/* Enhanced Search Box */}
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search posts..."
                  className="w-56 pl-4 pr-11 py-2.5 rounded-xl border-2 border-gray-200 bg-white/80 backdrop-blur-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none text-sm placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  aria-label="Search posts"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 transition-all"
                >
                  <Search className="w-4 h-4" />
                </button>
              </form>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors" 
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              <div className="icon-container">
                <div className={`menu-icon ${mobileMenuOpen ? 'hidden-icon' : ''}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-16 6h16"></path>
                  </svg>
                </div>
                <div className={`close-icon ${!mobileMenuOpen ? 'hidden-icon' : ''}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`lg:hidden ${!mobileMenuOpen ? 'hidden' : ''} ${mobileMenuOpen ? 'slide-down' : 'slide-up'}`} aria-label="Mobile navigation">
        <div className="bg-white mx-6 mt-2 p-6 px-4 rounded-xl">
          <nav className="flex flex-col space-y-4" role="navigation">
            {/* Enhanced Search Box for Mobile */}
            <form onSubmit={handleSearch} className="relative mb-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posts..."
                className="w-full pl-4 pr-14 py-3 rounded-xl border-2 border-gray-200 bg-white/80 backdrop-blur-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none placeholder:text-gray-400"
              />
              <button
                type="submit"
                aria-label="Search posts"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 transition-all"
              >
                <Search className="w-5 h-5" />
              </button>
            </form>

            <Link to="/posts" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group" onClick={() => setMobileMenuOpen(false)}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                <i className="fas fa-newspaper gradient-text text-sm"></i>
              </div>
              <div className="flex-1 pl-2">
                <span className="font-medium text-gray-700">Posts</span>
                <p className="text-sm text-gray-500">Articles & tutorials</p>
              </div>
            </Link>

            <Link to="/projects" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group" onClick={() => setMobileMenuOpen(false)}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                <i className="fas fa-project-diagram gradient-text text-sm"></i>
              </div>
              <div className="flex-1 pl-2">
                <span className="font-medium text-gray-700">Projects</span>
                <p className="text-sm text-gray-500">Explore all projects</p>
              </div>
            </Link>

            <Link to="/languages" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group" onClick={() => setMobileMenuOpen(false)}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                <i className="fas fa-code gradient-text text-sm"></i>
              </div>
              <div className="flex-1 pl-2">
                <span className="font-medium text-gray-700">Languages</span>
                <p className="text-sm text-gray-500">Programming languages</p>
              </div>
            </Link>

            <Link to="/categories" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group" onClick={() => setMobileMenuOpen(false)}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                <i className="fas fa-th-large gradient-text text-sm"></i>
              </div>
              <div className="flex-1 pl-2">
                <span className="font-medium text-gray-700">Categories</span>
                <p className="text-sm text-gray-500">Browse categories</p>
              </div>
            </Link>

            {/* About and Contact Buttons Row */}
            <div className="grid grid-cols-2 gap-2 mt-2 pt-3 border-t border-gray-100">
              <Link 
                to="/about" 
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-blue-500 hover:text-blue-500 transition-all text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="fas fa-info-circle text-xs"></i>
                <span>About</span>
              </Link>
              <Link 
                to="/contact" 
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-blue-500 hover:text-blue-500 transition-all text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="fas fa-envelope text-xs"></i>
                <span>Contact</span>
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
